import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

const KL = { odc: "ODC", odp: "ODP", otb: "OTB" };

// Alasan endpoint tidak dapat dipilih (null = boleh): anggota kontainer tanpa lokasi / tanpa koordinat.
export const endpointBlock = (o) => (o.in_server && !o.in_server.has_coords ? `Lokasi ${o.in_server.label} ${o.in_server.kode} belum ditentukan`
  : !o.has_coords ? "Lokasi peta belum ditentukan" : null);

function EndpointRow({ o, onPick, tid }) {
  const block = endpointBlock(o);
  return (
    <li>
      <button type="button" disabled={!!block} onClick={() => onPick(o)} data-testid={tid}
              className="flex w-full flex-wrap items-center gap-1.5 rounded px-2 py-1.5 text-left text-xs hover:bg-[#EFF6FF] disabled:cursor-not-allowed disabled:opacity-60">
        <span className="rounded bg-[#1E293B] px-1 text-[10px] font-bold text-white">{KL[o.kind]}</span>
        <b className="font-mono-tab">{o.kode}</b><span className="text-[#6B7280]">{o.nama}</span>
        {o.in_server && <span className="whitespace-nowrap rounded bg-[#F1F5F9] px-1 text-[10px] text-[#334155]" data-testid={`${tid}-container`}>di dalam {o.in_server.kode}</span>}
        {block && <span className="w-full text-[10px] font-semibold text-[#B45309]" data-testid={`${tid}-blocked`}>{block}</span>}
      </button>
    </li>
  );
}

export function ContainerPickPanel({ container, items, onPick, onClose }) {
  const ms = items.filter((o) => o.in_server?.id === container.id);
  return (
    <div className="absolute right-2 top-2 z-[1000] w-72 rounded-lg border border-[#E5E7EB] bg-white p-2 shadow-lg" data-testid="container-pick-panel">
      <div className="mb-1 flex items-center justify-between text-xs font-bold">
        <span>Pilih perangkat di dalam {container.label} <span className="font-mono-tab">{container.kode}</span></span>
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onClose} data-testid="container-pick-close"><X className="h-3.5 w-3.5" /></Button>
      </div>
      <ul className="max-h-56 overflow-y-auto">
        {ms.map((o) => <EndpointRow key={`${o.kind}-${o.id}`} o={o} onPick={onPick} tid={`container-pick-${o.kind}-${o.kode}`} />)}
      </ul>
      {!ms.length && <p className="p-2 text-xs text-[#6B7280]" data-testid="container-pick-empty">Tidak ada ODC/ODP/OTB di dalam {container.kode} yang dapat menjadi ujung kabel.</p>}
      <p className="mt-1 text-[10px] text-[#6B7280]">Kabel menyimpan perangkat asli; garis digambar dari titik {container.label}.</p>
    </div>
  );
}

export function EndpointSearch({ items, onPick }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(true);
  const box = useRef(null);
  useEffect(() => {  // tutup daftar saat klik di luar atau Escape (hanya saat daftar terbuka)
    if (!open) return undefined;
    const out = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", out);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("pointerdown", out); document.removeEventListener("keydown", esc); };
  }, [open]);
  const s = q.trim().toLowerCase();
  const hits = s ? items.filter((o) => [o.kode, o.nama].some((v) => (v || "").toLowerCase().includes(s))).slice(0, 8) : [];
  return (
    <div ref={box} className="relative mt-2" data-testid="endpoint-search">
      <Input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} placeholder="Cari endpoint (kode/nama ODC, ODP, OTB)…" className="h-9 text-xs" data-testid="endpoint-search-input" />
      {s && open && <ul className="absolute left-0 right-0 z-[1100] mt-1 max-h-48 overflow-y-auto rounded border border-[#E5E7EB] bg-white shadow-lg" data-testid="endpoint-search-results">
        {hits.map((o) => <EndpointRow key={`${o.kind}-${o.id}`} o={o} onPick={(x) => { onPick(x); setQ(""); }} tid={`endpoint-option-${o.kind}-${o.kode}`} />)}
        {!hits.length && <li className="p-2 text-xs text-[#6B7280]" data-testid="endpoint-search-empty">Tidak ditemukan.</li>}
      </ul>}
    </div>
  );
}
